import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface CommentAttachment {
  url: string;
  name: string;
  type: 'image' | 'pdf' | 'file';
}

export interface BoardTaskComment {
  id: string;
  task_id: string;
  user_id: string;
  content: string;
  reassigned_to: string | null;
  created_at: string;
  attachments: CommentAttachment[];
  mentions: string[];
  author?: {
    first_name: string | null;
    last_name: string | null;
  };
  reassigned_to_profile?: {
    first_name: string | null;
    last_name: string | null;
  };
}

/** Determine table + real ID based on cn_ prefix */
function resolveTask(taskId: string) {
  if (taskId.startsWith('cn_')) {
    return { table: 'course_notes_comments' as const, realId: taskId.slice(3) };
  }
  return { table: 'board_task_comments' as const, realId: taskId };
}

export function useBoardTaskComments(taskId: string | undefined) {
  return useQuery({
    queryKey: ['board-task-comments', taskId],
    enabled: !!taskId,
    queryFn: async () => {
      const { table, realId } = resolveTask(taskId!);

      const { data, error } = await supabase
        .from(table)
        .select('*')
        .eq('task_id', realId)
        .order('created_at', { ascending: true });
      if (error) throw error;

      // Fetch profiles for authors + reassigned users
      const userIds = new Set<string>();
      for (const c of data) {
        userIds.add(c.user_id);
        if (c.reassigned_to) userIds.add(c.reassigned_to);
      }

      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, first_name, last_name')
        .in('user_id', Array.from(userIds));

      const profileMap = new Map(
        (profiles || []).map((p) => [p.user_id, p])
      );

      return data.map((c) => ({
        ...c,
        attachments: (c.attachments as unknown as CommentAttachment[] | null) || [],
        mentions: (c.mentions as string[] | null) || [],
        author: profileMap.get(c.user_id) || null,
        reassigned_to_profile: c.reassigned_to
          ? profileMap.get(c.reassigned_to) || null
          : null,
      })) as BoardTaskComment[];
    },
  });
}

export function useCreateBoardTaskComment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (params: {
      taskId: string;
      content: string;
      reassignedTo?: string | null;
      attachments?: CommentAttachment[];
      mentions?: string[];
      taskTitle?: string;
      silent?: boolean;
    }) => {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) throw new Error('Nicht angemeldet');

      const { table, realId } = resolveTask(params.taskId);

      const mentions = (params.mentions || []).filter((id) => id && id !== user.id);

      const { data: insertedComment, error } = await supabase.from(table).insert({
        task_id: realId,
        user_id: user.id,
        content: params.content,
        reassigned_to: params.reassignedTo || null,
        mentions,
        attachments: params.attachments && params.attachments.length > 0
          ? JSON.parse(JSON.stringify(params.attachments))
          : [],
      }).select('id').single();
      if (error) throw error;

      // If reassigning, update the task's assigned_to
      if (params.reassignedTo) {
        const targetTable = table === 'course_notes_comments' ? 'course_notes' : 'board_tasks';
        const { error: updateError } = await supabase
          .from(targetTable)
          .update({ assigned_to: params.reassignedTo })
          .eq('id', realId);
        if (updateError) throw updateError;
      }

      // Erwähnungs-Mails versenden (außer bei stillen System-Kommentaren). Board-Aufgaben meldet
      // die Datenbank selbst (Trigger → board_notifications → planung-mails), hier nur Kurs-Aufgaben.
      if (!params.silent && mentions.length > 0 && params.taskTitle && table === 'course_notes_comments') {
        supabase.functions.invoke('send-task-mention-email', {
          body: {
            mentionedUserIds: mentions,
            taskId: params.taskId,
            taskTitle: params.taskTitle,
            commentExcerpt: params.content.slice(0, 500),
            authorUserId: user.id,
          },
        })
          .then(({ error }) => {
            if (error) {
              console.error('Mention-Mail Fehler:', error);
              toast.warning('Erwähnungs-Benachrichtigung konnte nicht gesendet werden.');
            }
          })
          .catch((e) => {
            console.error('Mention-Mail Fehler:', e);
            toast.warning('Erwähnungs-Benachrichtigung konnte nicht gesendet werden.');
          });
      }


      return { id: insertedComment.id };
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['board-task-comments', vars.taskId] });
      qc.invalidateQueries({ queryKey: ['task-latest-comments'] });
      qc.invalidateQueries({ queryKey: ['board-tasks'] });
      qc.invalidateQueries({ queryKey: ['course-notes-as-board-tasks'] });
      qc.invalidateQueries({ queryKey: ['course-notes'] });
      qc.invalidateQueries({ queryKey: ['planung-benachrichtigungen'] });
      if (!vars.silent) {
        toast.success(vars.reassignedTo ? 'Kommentar gesendet & Aufgabe zugewiesen' : 'Kommentar gesendet');
      }
    },
    onError: (error) => {
      console.error('Fehler beim Senden des Aufgabenkommentars:', error);
      toast.error('Fehler beim Senden des Kommentars');
    },
  });
}

/** Upload a file for task comments to community-uploads bucket */
export async function uploadTaskCommentFile(file: File): Promise<CommentAttachment> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user) throw new Error('Nicht angemeldet');

  const ext = file.name.split('.').pop()?.toLowerCase() || 'bin';
  const fileName = `task-comments/${session.user.id}/${crypto.randomUUID()}.${ext}`;

  const { error } = await supabase.storage
    .from('community-uploads')
    .upload(fileName, file);
  if (error) throw error;

  const { data } = supabase.storage
    .from('community-uploads')
    .getPublicUrl(fileName);

  const type: CommentAttachment['type'] = file.type.startsWith('image/')
    ? 'image'
    : ext === 'pdf'
      ? 'pdf'
      : 'file';

  return { url: data.publicUrl, name: file.name, type };
}
